import { Box, Grid, IconButton, lighten, makeStyles, Theme, Typography } from '@material-ui/core';
import { ArrowBack, ChevronRight } from '@material-ui/icons';
import { useDocumentStore } from '@roc/feature-documents';
import { Button, Paper, PaperBorderType } from '@roc/ui';
import { formatCurrency } from '@roc/ui/utils';
import { isNil } from '@roc/feature-utils';
import { observer } from 'mobx-react';
import { useEffect, useRef } from 'react';
import { PayoffCardCellText } from '../../payoffCalculator/subComponents/cardComponents/payoffCardCellText';
import { PayoffCardColumn } from '../../payoffCalculator/subComponents/cardComponents/payoffCardColumn';
import { PayoffCardRow } from '../../payoffCalculator/subComponents/cardComponents/payoffCardRow';
import { PayoffCardTotalCellText } from '../../payoffCalculator/subComponents/cardComponents/payoffCardTotalCellText';
import { PayoffCardTotalRow } from '../../payoffCalculator/subComponents/cardComponents/payoffCardTotalRow';
import { PaReconciliationColumn, SUMMARY_COLUMNS } from '../paReconciliationColumns';
import { PaReconciliationSummaryRow, PaReconciliationTotals } from '../paReconciliationTypes';
import { PaReconciliationInvoicesTable } from './paReconciliationInvoicesTable';

// Width split of a summary row: columns on the left, the chevron on the right. No min width: the summary
// must always fit the card without horizontal scroll, so the columns rely on percentages only.
const COLUMNS_WIDTH = '96%';
const ACTION_WIDTH = '4%';

const useStyles = makeStyles((theme: Theme) => ({
  body: {
    width: '100%',
    padding: '0px 16px 16px',
  },
  // Compact header text so long labels can wrap to two lines without the 35px line height of the calculator
  headerText: {
    fontWeight: 'bold',
    lineHeight: 1.25,
    whiteSpace: 'normal',
    padding: '6px 0px',
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      background: lighten(theme.palette.primary.main, 0.95),
    },
  },
  subtitle: {
    marginLeft: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

const SummaryColumn = observer(({ column, children = undefined }: { column: PaReconciliationColumn; children?: any }) => (
  <PayoffCardColumn width={column.width} textAlign={column.type === 'currency' ? 'right' : 'left'}>
    {children}
  </PayoffCardColumn>
));

const formatSummaryValue = (column: PaReconciliationColumn, values: Partial<PaReconciliationTotals> & { type?: string }) => {
  const value = values?.[column.field];
  if (isNil(value)) {
    return '-';
  }
  return column.type === 'currency' ? formatCurrency(Number(value)) : value;
};

const PaReconciliationHeaderRow = observer(() => {
  const classes = useStyles();

  return (
    <PayoffCardRow>
      <Box display="flex" alignItems="flex-start" style={{ width: COLUMNS_WIDTH }}>
        {SUMMARY_COLUMNS.map(column => (
          <SummaryColumn key={column.field} column={column}>
            <Typography variant="body2" className={classes.headerText}>
              {column.headerName}
            </Typography>
          </SummaryColumn>
        ))}
      </Box>
    </PayoffCardRow>
  );
});

// Clicking a Type row swaps the summary for that Type's invoices table
const PaReconciliationSummaryRowContent = observer(({ row, onClick }: { row: PaReconciliationSummaryRow; onClick: () => void }) => {
  const classes = useStyles();

  return (
    <Box onClick={onClick} className={classes.clickableRow} data-testid={`paReconciliationType-${row.type}`}>
      <PayoffCardRow>
        <Box display="flex" style={{ width: COLUMNS_WIDTH }}>
          {SUMMARY_COLUMNS.map(column => (
            <SummaryColumn key={column.field} column={column}>
              <PayoffCardCellText>{formatSummaryValue(column, row)}</PayoffCardCellText>
            </SummaryColumn>
          ))}
        </Box>
        <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ width: ACTION_WIDTH }}>
          <IconButton size="small" style={{ padding: 0 }}>
            <ChevronRight color="primary" />
          </IconButton>
        </Box>
      </PayoffCardRow>
    </Box>
  );
});

const PaReconciliationTotalRow = observer(() => {
  const {
    documentStore: {
      documentFormStore: { paReconciliationStore },
    },
  } = useDocumentStore();
  const values = { ...paReconciliationStore.totals, type: 'Total' };

  return (
    <PayoffCardTotalRow>
      <Box display="flex" style={{ width: COLUMNS_WIDTH }}>
        {SUMMARY_COLUMNS.map(column => (
          <SummaryColumn key={column.field} column={column}>
            <PayoffCardTotalCellText>{formatSummaryValue(column, values)}</PayoffCardTotalCellText>
          </SummaryColumn>
        ))}
      </Box>
    </PayoffCardTotalRow>
  );
});

const PaReconciliationSummary = observer(() => {
  const classes = useStyles();
  const {
    documentStore: {
      documentFormStore: { paReconciliationStore },
    },
  } = useDocumentStore();

  return (
    <Box className={classes.body}>
      <PaReconciliationHeaderRow />
      {paReconciliationStore.summaryRows.length > 0 ? (
        paReconciliationStore.summaryRows.map(row => (
          <PaReconciliationSummaryRowContent
            key={row.type}
            row={row}
            onClick={() => paReconciliationStore.setSelectedType(row.type)}
          />
        ))
      ) : (
        <PayoffCardRow>
          <SummaryColumn column={SUMMARY_COLUMNS[0]}>
            <PayoffCardCellText>No Records.</PayoffCardCellText>
          </SummaryColumn>
        </PayoffCardRow>
      )}
      <PaReconciliationTotalRow />
    </Box>
  );
});

export const PaReconciliationCard = observer(() => {
  const classes = useStyles();
  const {
    documentStore: {
      documentFormStore: { paReconciliationStore },
    },
  } = useDocumentStore();

  const selectedType = paReconciliationStore?.selectedType;
  const selectedCount = paReconciliationStore?.selectedTypeRecords?.length ?? 0;
  const cardRef = useRef<HTMLDivElement>(null);

  // Bring the invoices table into view when a Type is selected (the summary may have been scrolled far down)
  useEffect(() => {
    if (selectedType) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedType]);

  return (
    <div ref={cardRef}>
    <Paper
      titleComponent={
        <Box display="flex" p={2}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Box display="flex" alignItems="baseline">
                <Typography variant="h5">PA Reconciliation</Typography>
                {selectedType && (
                  <Typography variant="subtitle1" className={classes.subtitle}>
                    {`/ ${selectedType} (${selectedCount} ${selectedCount === 1 ? 'invoice' : 'invoices'})`}
                  </Typography>
                )}
              </Box>
            </Grid>
            {selectedType && (
              <Grid item>
                <Button
                  testId="paReconciliationBack"
                  onClick={() => paReconciliationStore.clearSelectedType()}
                  variant="outlined"
                  size="small"
                  color="primary"
                  startIcon={<ArrowBack />}
                >
                  Back
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
      }
      borderType={PaperBorderType.SOLID_BORDER}
    >
      {selectedType ? (
        <Box className={classes.body}>
          <PaReconciliationInvoicesTable type={selectedType} />
        </Box>
      ) : (
        <PaReconciliationSummary />
      )}
    </Paper>
    </div>
  );
});
